/**
 * Regras da tela do Backoffice do Faturamento na Visão Geral (#6501): Brenda.
 *
 * Pergunta da tela: o rateio do mês está certo, e as faturas e as usinas estão em dia?
 * Cada bloco reaproveita a regra da tela de origem:
 *   - rateio do mês: a planilha que monta a janela (mesma escolha de
 *     src/lib/planilhaRateioVigente.ts) e o prazo do aviso da Vick (dia 5);
 *   - faturas que não chegaram: a Conferência do Faturamento + a fila do robô da
 *     Equatorial, com o motivo da falha classificado como na Janela;
 *   - titularidades: a troca assinada só termina quando a fatura da Equatorial sai
 *     no nome do cliente (o protocolo na Equatorial ainda não é registrado);
 *   - usinas: a mesma função do aviso de usina parada, as interrupções da aba
 *     Análise e a régua "irmãs" do ranking.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/backoffice.test.ts
 */
import { mesDeOrdem, normUc, ordemMes, type LinhaConferencia } from '../conferenciaFaturamento.ts';
import { motivoDoRobo, type MotivoRobo } from '../../../views/Financeiro/lib/janelaFaturamentoLista.ts';
import { capacidadeKwp, classificarDesvio, referenciaEsperada, type StatusDesvio } from '../../../views/Geradoras/geradorasSeries.ts';
import { getRotulosCurtos } from '../../../views/Geradoras/geradorasShared.ts';

export const META_FATURAS_EMAIL = 0.85;

/**
 * A planilha de rateio do mês M monta a janela de faturamento de M+1 e precisa estar no
 * sistema até o dia 5 de M+1: as primeiras faturas chegam entre o dia 6 e o 9. Mesmo
 * dia do aviso da Vick (`DIA_PLANILHA_RATEIO_ATRASADA` em supabase/functions/_shared/vigia/detectores.ts).
 */
export const DIA_PRAZO_PLANILHA = 5;

const DIA_MS = 86_400_000;

/** Data local sem hora. */
const dia = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isoDia = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Dias de calendário de `a` até `b` (positivo quando `b` é depois). */
const diasEntre = (a: Date, b: Date): number => Math.round((dia(b).getTime() - dia(a).getTime()) / DIA_MS);
const soDigitos = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '').replace(/^0+/, '');

// ─────────────────────────── Rateio do mês ───────────────────────────

export interface PlanilhaCarregada {
  id: string;
  mes_referencia: string | null;
  created_at: string;
  usuario_nome?: string | null;
}

/**
 * Carga feita à mão como cópia de outro mês (OUT/2026 abriu com a planilha de AGO copiada
 * como SET, #6283). O sistema marca no nome de quem carregou: "Felipe Braz (cópia de AGO/2026)".
 */
export const ehCopia = (p: Pick<PlanilhaCarregada, 'usuario_nome'>): boolean => /c[óo]pia/i.test(p.usuario_nome ?? '');

const doMes = (planilhas: readonly PlanilhaCarregada[], ordem: number) =>
  planilhas
    .filter(p => ordemMes(p.mes_referencia) === ordem)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

/** Prazo da planilha do mês de ordem `ordem`: dia 5 do mês seguinte. */
const prazoDaPlanilha = (ordem: number): Date => new Date(Math.floor((ordem + 1) / 12), (ordem + 1) % 12, DIA_PRAZO_PLANILHA);

export type EstadoRateio =
  | 'em_dia'      // a planilha que vale para a janela do mês subiu
  | 'copia'       // só existe uma cópia de outro mês no lugar dela
  | 'aguardando'  // ainda não subiu, mas o prazo não passou
  | 'repetindo';  // não subiu e o prazo passou: a janela repete a última planilha

export interface SituacaoRateio {
  /** Mês das faturas da janela atual ('OUT/2026'). */
  mesFaturas: string;
  /** Planilha que vale para essas faturas ('SET/2026'). */
  planilhaEsperada: string;
  estado: EstadoRateio;
  /** A planilha que a janela usa agora (a esperada, a cópia ou a última repetida). */
  usada: { mes: string; carregadaEm: string; por: string | null; copia: boolean } | null;
  prazo: string;
  /** Dias até o prazo (negativo = passou). */
  diasParaPrazo: number;
  /** A planilha deste mês, que vai montar a janela do mês que vem. */
  proxima: { mes: string; prazo: string; diasParaPrazo: number; jaSubiu: boolean };
}

export function situacaoDoRateio(planilhas: readonly PlanilhaCarregada[], hoje: Date = new Date()): SituacaoRateio {
  const ordemAtual = hoje.getFullYear() * 12 + hoje.getMonth();
  const ordemEsperada = ordemAtual - 1;
  const esperadas = doMes(planilhas, ordemEsperada);
  const real = esperadas.find(p => !ehCopia(p)) ?? null;
  const copia = esperadas.find(ehCopia) ?? null;
  const prazo = prazoDaPlanilha(ordemEsperada);
  const diasParaPrazo = diasEntre(hoje, prazo);

  let escolhida: PlanilhaCarregada | null = real ?? copia;
  if (!escolhida) {
    // Mesma regra da Janela: repete a planilha mais recente antes da esperada.
    escolhida = planilhas
      .filter(p => (ordemMes(p.mes_referencia) ?? Infinity) < ordemEsperada)
      .sort((a, b) => (ordemMes(b.mes_referencia)! - ordemMes(a.mes_referencia)!) || b.created_at.localeCompare(a.created_at))[0] ?? null;
  }
  const estado: EstadoRateio = real ? 'em_dia' : copia ? 'copia' : diasParaPrazo >= 0 ? 'aguardando' : 'repetindo';

  const prazoProxima = prazoDaPlanilha(ordemAtual);
  return {
    mesFaturas: mesDeOrdem(ordemAtual),
    planilhaEsperada: mesDeOrdem(ordemEsperada),
    estado,
    usada: escolhida
      ? { mes: String(escolhida.mes_referencia), carregadaEm: escolhida.created_at, por: escolhida.usuario_nome ?? null, copia: ehCopia(escolhida) }
      : null,
    prazo: isoDia(prazo),
    diasParaPrazo,
    proxima: {
      mes: mesDeOrdem(ordemAtual),
      prazo: isoDia(prazoProxima),
      diasParaPrazo: diasEntre(hoje, prazoProxima),
      jaSubiu: doMes(planilhas, ordemAtual).some(p => !ehCopia(p)),
    },
  };
}

export type EstadoPrazoPlanilha = 'no_prazo' | 'atrasada' | 'so_copia' | 'nao_subiu';

export interface PrazoDaPlanilha {
  mes: string;
  prazo: string;
  /** Primeira carga de verdade (sem contar cópia). */
  subiuEm: string | null;
  estado: EstadoPrazoPlanilha;
  diasAtraso: number | null;
}

/**
 * As últimas `quantos` planilhas cujo prazo já passou, cada uma contra o dia 5 do mês
 * seguinte. Conta a data em que a planilha subiu no sistema: o envio à Equatorial
 * ainda não é registrado.
 */
export function planilhasNoPrazo(
  planilhas: readonly PlanilhaCarregada[],
  hoje: Date = new Date(),
  quantos = 3,
): { meses: PrazoDaPlanilha[]; noPrazo: number } {
  const ordemAtual = hoje.getFullYear() * 12 + hoje.getMonth();
  // No dia 5 o prazo de M-1 ainda não venceu: a última conta é M-2.
  const ultima = hoje.getDate() > DIA_PRAZO_PLANILHA ? ordemAtual - 1 : ordemAtual - 2;
  const meses: PrazoDaPlanilha[] = [];
  for (let o = ultima; o > ultima - quantos; o--) {
    const prazo = prazoDaPlanilha(o);
    const cargas = doMes(planilhas, o);
    const reais = cargas.filter(p => !ehCopia(p)).sort((a, b) => a.created_at.localeCompare(b.created_at));
    const primeira = reais[0] ?? null;
    if (!primeira) {
      meses.push({ mes: mesDeOrdem(o), prazo: isoDia(prazo), subiuEm: null, estado: cargas.length ? 'so_copia' : 'nao_subiu', diasAtraso: null });
      continue;
    }
    const atraso = diasEntre(prazo, new Date(primeira.created_at));
    meses.push({
      mes: mesDeOrdem(o),
      prazo: isoDia(prazo),
      subiuEm: primeira.created_at,
      estado: atraso > 0 ? 'atrasada' : 'no_prazo',
      diasAtraso: atraso > 0 ? atraso : null,
    });
  }
  return { meses, noPrazo: meses.filter(m => m.estado === 'no_prazo').length };
}

// ─────────────────────────── Faturas pelo e-mail ───────────────────────────

const chavesDaLinha = (l: Pick<LinhaConferencia, 'chave' | 'uc' | 'outroNumeroUc'>): string[] =>
  [l.chave, normUc(l.uc), normUc(l.outroNumeroUc)].filter((c): c is string => !!c);

const faturaRecebida = (l: LinhaConferencia) => l.quantidadeFaturas > 0 && l.detalhe !== 'fatura_nao_recebida';

/**
 * Das UCs do rateio com a fatura do mês já no sistema, quantas receberam a fatura pelo
 * e-mail da Vigor (robô do e-mail, `extracoes_faturas.origem = 'robo-assinatura'`).
 * Vale qualquer chegada pelo e-mail no mês, mesmo depois de um download manual: o que
 * se mede é a UC estar cadastrada na Equatorial para mandar a fatura ao e-mail.
 */
export function faturasPeloEmail(
  linhas: readonly LinhaConferencia[],
  ucsComEmail: ReadonlySet<string>,
): { recebidas: number; pelosEmail: number; taxa: number | null } {
  const recebidas = linhas.filter(faturaRecebida);
  const pelosEmail = recebidas.filter(l => chavesDaLinha(l).some(c => ucsComEmail.has(c))).length;
  return { recebidas: recebidas.length, pelosEmail, taxa: recebidas.length ? pelosEmail / recebidas.length : null };
}

// ─────────────────────────── Faturas que não chegaram ───────────────────────────

export interface EntradaFilaRobo {
  uc_codigo: string | null;
  mes_referencia: string | null;
  status: string | null;
  error_details: string | null;
  updated_at: string | null;
}

export type SituacaoRobo = 'na_fila' | 'falhou' | 'fora_da_fila';

export interface FaturaQueNaoChegou {
  linha: LinhaConferencia;
  robo: SituacaoRobo;
  motivo: MotivoRobo | null;
  tentativaEm: string | null;
}

/**
 * Sem tentativa do robô há mais que isso, com UC esperando na fila, o robô está parado.
 * Em 15/10/2026 ele parou e ninguém percebeu por 13 dias (#6513).
 */
export const HORAS_ROBO_PARADO = 24;

export interface ResumoNaoChegaram {
  lista: FaturaQueNaoChegou[];
  /**
   * Última vez que o robô mexeu numa UC do mês (falha ou em andamento). "concluido"
   * fica de fora porque o cron das 03h também conclui tarefas, sem o robô.
   */
  ultimaTentativaRobo: string | null;
  total: number;
  /** Passaram do prazo de chegada (17 dias depois da leitura, o p90 da Conferência). */
  atrasadas: number;
  naFila: number;
  falhou: number;
  foraDaFila: number;
  porMotivo: Partial<Record<MotivoRobo, number>>;
}

/**
 * UCs do rateio sem a fatura do mês, com o que o robô da Equatorial fez com cada uma.
 * A fila tem várias linhas por UC e mês (cliques repetidos em "Equatorial"): vale a mais
 * recente. Falha de "fatura já cadastrada" não aparece aqui porque a UC já tem fatura.
 */
export function faturasQueNaoChegaram(
  linhas: readonly LinhaConferencia[],
  fila: readonly EntradaFilaRobo[],
  mes: string,
): ResumoNaoChegaram {
  const ultimaPorUc = new Map<string, EntradaFilaRobo>();
  for (const f of fila) {
    if ((f.mes_referencia ?? '').toUpperCase() !== mes.toUpperCase()) continue;
    const chave = normUc(f.uc_codigo);
    if (!chave) continue;
    const atual = ultimaPorUc.get(chave);
    if (!atual || (f.updated_at ?? '') > (atual.updated_at ?? '')) ultimaPorUc.set(chave, f);
  }

  const lista: FaturaQueNaoChegou[] = linhas
    .filter(l => l.detalhe === 'fatura_nao_recebida')
    .map(linha => {
      const entrada = chavesDaLinha(linha).map(c => ultimaPorUc.get(c)).find(Boolean) ?? null;
      const status = entrada?.status ?? null;
      // "concluido" sem fatura no mês: o cron das 03h fecha a tarefa quando acha a fatura
      // por outro caminho; se a fatura não está aqui, o robô não está mais tentando.
      const robo: SituacaoRobo = status === 'pendente' || status === 'processando' ? 'na_fila' : status === 'falhou' ? 'falhou' : 'fora_da_fila';
      return { linha, robo, motivo: robo === 'falhou' ? motivoDoRobo(entrada?.error_details) : null, tentativaEm: entrada?.updated_at ?? null };
    })
    .sort((a, b) =>
      Number(b.linha.chegadaAtrasada) - Number(a.linha.chegadaAtrasada)
      || (a.linha.chegadaPrevista ?? '9999').localeCompare(b.linha.chegadaPrevista ?? '9999'));

  const porMotivo: Partial<Record<MotivoRobo, number>> = {};
  for (const f of lista) if (f.motivo) porMotivo[f.motivo] = (porMotivo[f.motivo] ?? 0) + 1;
  const ultimaTentativaRobo = fila
    .filter(f => (f.mes_referencia ?? '').toUpperCase() === mes.toUpperCase() && (f.status === 'falhou' || f.status === 'processando'))
    .reduce<string | null>((m, f) => (f.updated_at && (!m || f.updated_at > m) ? f.updated_at : m), null);
  return {
    lista,
    ultimaTentativaRobo,
    total: lista.length,
    atrasadas: lista.filter(f => f.linha.chegadaAtrasada).length,
    naFila: lista.filter(f => f.robo === 'na_fila').length,
    falhou: lista.filter(f => f.robo === 'falhou').length,
    foraDaFila: lista.filter(f => f.robo === 'fora_da_fila').length,
    porMotivo,
  };
}

/** O robô está parado: há UC esperando na fila e nenhuma tentativa nas últimas 24 h. */
export function roboParado(r: Pick<ResumoNaoChegaram, 'naFila' | 'ultimaTentativaRobo'>, agora: Date = new Date()): boolean {
  if (r.naFila === 0) return false;
  if (!r.ultimaTentativaRobo) return true;
  return agora.getTime() - new Date(r.ultimaTentativaRobo).getTime() > HORAS_ROBO_PARADO * 3_600_000;
}

// ─────────────────────────── Titularidades ───────────────────────────

export interface TrocaTitularidade {
  id: number;
  negociacao_id: number;
  uc_id: number | null;
  uc_numero: string | null;
  cliente_nome: string | null;
  cpf_cnpj_cliente: string | null;
  assinado_em: string | null;
  created_at: string;
}

/** Fatura da Equatorial lida do PDF: o CPF/CNPJ é o do titular da conta. */
export interface FaturaDoTitular {
  uc_codigo_norm: string | null;
  cpf_cnpj: string | null;
  /** 'AAAA-MM'. */
  mes_referencia: string | null;
  created_at: string;
}

export type EstadoTroca =
  | 'aguardando_assinatura'  // documento enviado, o cliente ainda não assinou
  | 'sem_fatura_nova'        // assinada, nenhuma fatura de mês seguinte ainda
  | 'fatura_no_nome_antigo'  // já veio fatura de mês seguinte e ela não está no nome do cliente
  | 'efetivada';             // veio fatura no nome do cliente

export interface TrocaAcompanhada {
  troca: TrocaTitularidade;
  negociacao: { status: string | null; titulo: string | null } | null;
  estado: EstadoTroca;
  diasDesdeAssinatura: number | null;
  /** A fatura mais recente depois da assinatura. */
  ultimaFatura: FaturaDoTitular | null;
}

/**
 * Negociação com o contrato (termo) assinado: só aí a troca vira trabalho do backoffice.
 * Termo reprovado, simulação e termo ainda não assinado ficam de fora (contados à parte).
 */
export const STATUS_TERMO_ASSINADO: ReadonlySet<string> = new Set(['termo_assinado', 'termo_analise', 'termo_aprovado']);

const mesIsoDaData = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Trocas de titularidade assinadas que ainda não aparecem na fatura da Equatorial.
 *
 * Fim da troca = fatura no nome do cliente (CPF/CNPJ do PDF igual ao do documento
 * assinado). Fatura do mesmo mês da assinatura no nome antigo é esperada (a leitura veio
 * antes da troca); só fatura de mês seguinte no nome antigo indica que a troca não
 * andou. Só entram negociações com o termo assinado (`STATUS_TERMO_ASSINADO`); as
 * demais vão para `semTermoAssinado`. `numerosDaUc` traz os dois números da UC (antigo e
 * novo) para achar a fatura mesmo quando o documento usou o outro.
 */
export function acompanharTitularidades(
  trocas: readonly TrocaTitularidade[],
  faturas: readonly FaturaDoTitular[],
  negociacoes: ReadonlyMap<number, { status: string | null; titulo: string | null }>,
  numerosDaUc: ReadonlyMap<number, string[]> = new Map(),
  hoje: Date = new Date(),
): { pendentes: TrocaAcompanhada[]; assinadas: TrocaAcompanhada[]; aguardandoAssinatura: number; efetivadas: number; semTermoAssinado: number } {
  // Vale o último documento de cada UC em cada negociação (reenvio substitui).
  const ultimas = new Map<string, TrocaTitularidade>();
  for (const t of trocas) {
    const chave = `${t.negociacao_id}|${t.uc_id ?? normUc(t.uc_numero) ?? t.id}`;
    const atual = ultimas.get(chave);
    if (!atual || t.created_at > atual.created_at) ultimas.set(chave, t);
  }

  const faturasPorUc = new Map<string, FaturaDoTitular[]>();
  for (const f of faturas) {
    const c = normUc(f.uc_codigo_norm);
    if (!c) continue;
    if (!faturasPorUc.has(c)) faturasPorUc.set(c, []);
    faturasPorUc.get(c)!.push(f);
  }

  const todas: TrocaAcompanhada[] = [];
  let semTermoAssinado = 0;
  for (const troca of ultimas.values()) {
    const negociacao = negociacoes.get(troca.negociacao_id) ?? null;
    if (negociacao?.status === 'perdido') continue;
    if (!STATUS_TERMO_ASSINADO.has(negociacao?.status ?? '')) { semTermoAssinado++; continue; }
    if (!troca.assinado_em) {
      todas.push({ troca, negociacao, estado: 'aguardando_assinatura', diasDesdeAssinatura: null, ultimaFatura: null });
      continue;
    }
    const numeros = new Set([troca.uc_numero, ...(troca.uc_id != null ? numerosDaUc.get(troca.uc_id) ?? [] : [])]
      .map(n => normUc(n)).filter((n): n is string => !!n));
    const depois = [...numeros].flatMap(n => faturasPorUc.get(n) ?? [])
      .filter(f => f.created_at > troca.assinado_em!)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const doc = soDigitos(troca.cpf_cnpj_cliente);
    const mesAssinatura = mesIsoDaData(troca.assinado_em);
    const estado: EstadoTroca = doc && depois.some(f => soDigitos(f.cpf_cnpj) === doc)
      ? 'efetivada'
      : depois.some(f => (f.mes_referencia ?? '') > mesAssinatura) ? 'fatura_no_nome_antigo' : 'sem_fatura_nova';
    todas.push({ troca, negociacao, estado, diasDesdeAssinatura: diasEntre(new Date(troca.assinado_em), hoje), ultimaFatura: depois[0] ?? null });
  }

  const pendentes = todas
    .filter(t => t.estado === 'sem_fatura_nova' || t.estado === 'fatura_no_nome_antigo')
    .sort((a, b) => (b.diasDesdeAssinatura ?? 0) - (a.diasDesdeAssinatura ?? 0));
  return {
    pendentes,
    // Todas as assinadas, efetivadas inclusive: base da "titularidade em 3 dias úteis" (#6509).
    assinadas: todas.filter(t => t.estado !== 'aguardando_assinatura'),
    aguardandoAssinatura: todas.filter(t => t.estado === 'aguardando_assinatura').length,
    efetivadas: todas.filter(t => t.estado === 'efetivada').length,
    semTermoAssinado,
  };
}

// ─────────────────────────── Usinas agora ───────────────────────────

export interface UsinaCadastro {
  site_id_api: string;
  nome_usina: string;
  capacidade_instalada: number | string | null;
}

export interface UsinaSemReportar {
  site_id_api: string;
  falha: 'sem_leitura' | 'sem_geracao' | 'nunca_reportou' | string;
  horas_paradas: number | null;
  ultima_geracao: string | null;
}

export interface InterrupcaoAberta {
  site_id_api: string;
  tipo: string;
  inicio: string;
  em_aberto: boolean;
  a_confirmar: boolean;
  pct_da_referencia: number | null;
}

export interface UsinaAgora {
  site: string;
  nome: string;
  semReportar: UsinaSemReportar | null;
  interrupcao: InterrupcaoAberta | null;
  /** Eventos de alarme das últimas 72 h sem fim registrado. */
  alarmes: number;
  /** Sem reportar, sem gerar ou com interrupção em aberto. Alarme sozinho não conta. */
  problema: boolean;
}

/** Ordem de gravidade das interrupções (a da aba Análise). */
const GRAVIDADE: Record<string, number> = { parada: 0, parcial: 1, sem_comunicacao: 2, sem_leitura: 3 };

/** Usinas cadastradas, uma por site, em ordem de nome, com o rótulo curto do Painel. */
export function usinasDoCadastro(unidades: readonly UsinaCadastro[]): { site: string; nome: string; kwp: number | null }[] {
  const porSite = new Map<string, UsinaCadastro>();
  unidades.forEach(u => porSite.set(u.site_id_api, u));
  const lista = [...porSite.values()].sort((a, b) => a.nome_usina.localeCompare(b.nome_usina, 'pt-BR'));
  const rotulos = getRotulosCurtos(lista);
  return lista.map(u => ({ site: u.site_id_api, nome: rotulos[u.site_id_api] || u.nome_usina, kwp: capacidadeKwp(Number(u.capacidade_instalada) || null) }));
}

/**
 * O "agora" das usinas para quem cuida delas. Alarme sozinho não acende a usina: a
 * Chint nunca avisa o fim do alarme, e quase toda usina ficaria em alerta por 72 h
 * (mesma decisão da Visão 3D). O que acende é parar de reportar, parar de gerar ou
 * ter interrupção em aberto.
 */
export function usinasAgora(entrada: {
  unidades: readonly UsinaCadastro[];
  semReportar: readonly UsinaSemReportar[];
  interrupcoes: readonly InterrupcaoAberta[];
  alarmesPorSite: ReadonlyMap<string, number>;
}): { usinas: UsinaAgora[]; comProblema: number; semReportar: number; interrompidas: number; comAlarme: number } {
  const semReportar = new Map(entrada.semReportar.map(s => [s.site_id_api, s]));
  const interrupcao = new Map<string, InterrupcaoAberta>();
  for (const i of entrada.interrupcoes) {
    if (!i.em_aberto) continue;
    const atual = interrupcao.get(i.site_id_api);
    if (!atual || (GRAVIDADE[i.tipo] ?? 9) < (GRAVIDADE[atual.tipo] ?? 9)) interrupcao.set(i.site_id_api, i);
  }

  const usinas = usinasDoCadastro(entrada.unidades).map(({ site, nome }): UsinaAgora => {
    const sr = semReportar.get(site) ?? null;
    const it = interrupcao.get(site) ?? null;
    return { site, nome, semReportar: sr, interrupcao: it, alarmes: entrada.alarmesPorSite.get(site) ?? 0, problema: !!sr || !!it };
  }).sort((a, b) => Number(b.problema) - Number(a.problema) || b.alarmes - a.alarmes || a.nome.localeCompare(b.nome, 'pt-BR'));

  return {
    usinas,
    comProblema: usinas.filter(u => u.problema).length,
    semReportar: usinas.filter(u => u.semReportar).length,
    interrompidas: usinas.filter(u => u.interrupcao).length,
    comAlarme: usinas.filter(u => u.alarmes > 0).length,
  };
}

// ─────────────────────────── Geração do dia ───────────────────────────

export interface GeracaoNoPeriodo {
  site_id_api: string;
  geracao_kwh: number | null;
}

/**
 * Acima disso por dia o acumulador saltou (caso da Qgbt1): a usina sai da referência.
 * Mesmo corte da tabela "Por que gerou menos" (views/Geradoras/interrupcoes.ts).
 */
export const LIMITE_KWH_KWP_DIA = 12;

export interface GeracaoDaUsina {
  site: string;
  nome: string;
  hoje: number | null;
  esperadoHoje: number | null;
  statusHoje: StatusDesvio;
  ontem: number | null;
  esperadoOntem: number | null;
  statusOntem: StatusDesvio;
}

const esperadoPelasIrmas = (
  usinas: { site: string; kwp: number | null }[],
  geracao: readonly GeracaoNoPeriodo[],
): Map<string, { real: number | null; esperado: number | null }> => {
  const real = new Map(geracao.map(g => [g.site_id_api, g.geracao_kwh == null ? null : Number(g.geracao_kwh)]));
  const kwhKwpPorSite: Record<string, number | null> = {};
  for (const u of usinas) {
    const r = real.get(u.site);
    const v = r != null && u.kwp ? r / u.kwp : null;
    kwhKwpPorSite[u.site] = v != null && v <= LIMITE_KWH_KWP_DIA ? v : null;
  }
  return new Map(usinas.map(u => {
    const ref = referenciaEsperada(u.site, 'irmas', { kwhKwpPorSite });
    return [u.site, { real: real.get(u.site) ?? null, esperado: ref != null && u.kwp ? ref * u.kwp : null }];
  }));
};

/**
 * Geração de hoje (até agora) e de ontem contra a esperada, por usina.
 *
 * Esperada = o que a usina geraria rendendo, por kWp, como a mediana das outras no
 * mesmo período (régua "irmãs" da aba Análise). Serve para o dia em andamento e
 * para todas as usinas; o laudo PVsyst só existe para os 5 Blocos de Morrinhos e é
 * mensal (fica na aba Esperado × Realizado).
 */
export function geracaoDoDia(
  unidades: readonly UsinaCadastro[],
  hoje: readonly GeracaoNoPeriodo[],
  ontem: readonly GeracaoNoPeriodo[],
): { usinas: GeracaoDaUsina[]; hoje: number; esperadoHoje: number; taxaHoje: number | null; ontem: number; esperadoOntem: number; taxaOntem: number | null; abaixoHoje: number } {
  const cadastro = usinasDoCadastro(unidades);
  const h = esperadoPelasIrmas(cadastro, hoje);
  const o = esperadoPelasIrmas(cadastro, ontem);
  const usinas = cadastro.map(({ site, nome }): GeracaoDaUsina => {
    const dh = h.get(site)!;
    const dO = o.get(site)!;
    return {
      site, nome,
      hoje: dh.real, esperadoHoje: dh.esperado, statusHoje: classificarDesvio(dh.real, dh.esperado),
      ontem: dO.real, esperadoOntem: dO.esperado, statusOntem: classificarDesvio(dO.real, dO.esperado),
    };
  });
  const soma = (pegar: (u: GeracaoDaUsina) => [number | null, number | null]) =>
    usinas.reduce((t, u) => {
      const [r, e] = pegar(u);
      return e == null ? t : { real: t.real + (r ?? 0), esperado: t.esperado + e };
    }, { real: 0, esperado: 0 });
  const th = soma(u => [u.hoje, u.esperadoHoje]);
  const to = soma(u => [u.ontem, u.esperadoOntem]);
  return {
    usinas,
    hoje: th.real, esperadoHoje: th.esperado, taxaHoje: th.esperado > 0 ? th.real / th.esperado : null,
    ontem: to.real, esperadoOntem: to.esperado, taxaOntem: to.esperado > 0 ? to.real / to.esperado : null,
    abaixoHoje: usinas.filter(u => u.statusHoje === 'critico').length,
  };
}

// ─────────────────────────── Quem entra e quem sai do rateio (#6506) ───────────────────────────

export interface ItemDaPlanilha {
  uc: string | null;
  outro_numero_uc: string | null;
  status: string | null;
}

export interface AprovadaParaRateio {
  negociacao_id: number;
  cliente: string | null;
  aprovadaEm: string;
  consumo_kwh: number | null;
  /** Números de UC da negociação e das UCs ligadas a ela. */
  numeros: (string | null)[];
}

export interface CancelamentoConcluido {
  id: number;
  cliente: string | null;
  uc_numero: string | null;
  numeros: (string | null)[];
  concluida_em: string | null;
}

/** Status da planilha em que a UC recebe energia ("1° Ciclo" vem com o sinal de grau). */
const recebeNaPlanilha = (status: string | null) => {
  const s = (status ?? '').trim().toLowerCase().replace(/[°º]/g, '').replace(/\s+/g, ' ');
  return s === 'ativa' || s === '1 ciclo' || s === '2 ciclo';
};

/**
 * Quem entra e quem sai do rateio, contra a planilha mais recente:
 *   - aprovadas fora: negociação aprovada sem nenhuma UC na planilha (entra no próximo envio);
 *   - canceladas dentro: cancelamento concluído com a UC ainda recebendo energia na planilha
 *     (sai no próximo envio). Na planilha como "Cancelada" já saiu.
 * As UCs para voltar ao rateio vêm do painel de Saldos.
 */
export function entraESaiDoRateio(
  itens: readonly ItemDaPlanilha[],
  aprovadas: readonly AprovadaParaRateio[],
  cancelamentos: readonly CancelamentoConcluido[],
  hojeIso: string,
): {
  aprovadasFora: (AprovadaParaRateio & { dias: number })[];
  canceladasDentro: (CancelamentoConcluido & { statusNaPlanilha: string })[];
} {
  const statusPorUc = new Map<string, string>();
  for (const i of itens) for (const n of [normUc(i.uc), normUc(i.outro_numero_uc)]) if (n) statusPorUc.set(n, i.status ?? '');
  const naPlanilha = (numeros: (string | null)[]) => numeros.map(n => normUc(n)).filter((n): n is string => !!n && statusPorUc.has(n));

  const aprovadasFora = aprovadas
    .filter(a => a.numeros.some(n => normUc(n)) && naPlanilha(a.numeros).length === 0)
    .map(a => ({ ...a, dias: diasEntre(new Date(a.aprovadaEm), new Date(`${hojeIso}T12:00:00`)) }))
    .sort((a, b) => b.dias - a.dias);

  const canceladasDentro = cancelamentos.flatMap(c => {
    const dentro = naPlanilha(c.numeros).map(n => statusPorUc.get(n)!).find(recebeNaPlanilha);
    return dentro ? [{ ...c, statusNaPlanilha: dentro }] : [];
  });
  return { aprovadasFora, canceladasDentro };
}
